import React, { FC, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import Typography from '../../typography/Typography';
import { chartColors } from '../hardware/AssetAgeChart';

export interface ChartDataItem {
  name: string;
  percentage: number;
  [key: string]: any;
}

interface BarChartData {
  data: ChartDataItem[];
  unit?: string;
  onLegendClick?: any;
  suffix?: string;
}

const InternalLegendItem = ({
  item,
  isActive,
  unit,
  onLegendClick,
  suffix,
}: {
  item: any;
  isActive: boolean;
  unit: string;
  onLegendClick?: any;
  suffix?: string;
}) => (
  <div
    onClick={() => onLegendClick && onLegendClick(item.name)}
    className={`flex items-center justify-between rounded-xl transition-all duration-200 `}
  >
    <div className={`flex items-center gap-3 ${isActive && 'scale-105'}`}>
      <div
        className="w-3 h-3 rounded-full shadow-sm"
        style={{ backgroundColor: item.color }}
      />
      <Typography tag="span" type="small">
        {item.name} {suffix}
      </Typography>
    </div>
    <Typography tag="span" type="small">
      {item.percentage}
      {unit}
    </Typography>
  </div>
);

const StatsDonutChart: FC<BarChartData> = ({
  data,
  unit = '%',
  onLegendClick,
  suffix = '',
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!data || !Array.isArray(data)) {
    return null; // Or a smaller loading state/skeleton
  }

  const coloredData = data.map((item, index) => ({
    ...item,
    color: item.color || chartColors[index % chartColors.length],
  }));

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  const totalValue = data.reduce(
    (acc, curr) => acc + Number(curr.percentage || 0),
    0
  );

  // Constants for legend visibility
  const LIMIT = 8;
  const shouldShowExpand = coloredData.length > LIMIT;
  const visibleData = isExpanded ? coloredData : coloredData.slice(0, LIMIT);

  return (
    <div className="flex flex-col items-center justify-center w-full max-w-2xl mx-auto">
      <div className="relative w-full h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={coloredData}
              dataKey="percentage"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={90}
              outerRadius={120}
              paddingAngle={6}
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
              stroke="none"
            >
              {coloredData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  onClick={() => onLegendClick && onLegendClick(entry)}
                  style={{
                    transition:
                      'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                  opacity={
                    activeIndex === index || activeIndex === null ? 1 : 0.4
                  }
                />
              ))}
            </Pie>
            <Tooltip
              cursor={false}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-gray-900 px-4 py-2 shadow-2xl rounded-xl text-xs font-bold text-white border border-gray-800">
                      {`${payload[0].name} ${suffix || ''}: ${payload[0].value}${unit}`}{' '}
                    </div>
                  );
                }
                return null;
              }}
            />
          </PieChart>
        </ResponsiveContainer>

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
          <Typography tag="p" type="title">
            {activeIndex !== null
              ? Number(coloredData[activeIndex].percentage).toFixed(0)
              : Number(totalValue).toFixed(0)}
            {unit}
          </Typography>

          <p className="text-[10px] text-gray-400 uppercase font-black tracking-[0.3em] mt-1 transition-all duration-300">
            {activeIndex !== null ? coloredData[activeIndex].name : 'Overview'}
          </p>
        </div>
      </div>

      <div className="w-full px-8 mb-4">
        <div className="flex flex-col gap-1 transition-all duration-300">
          {visibleData.map((item, index) => (
            <div
              key={index}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
              className="cursor-pointer"
            >
              <InternalLegendItem
                item={item}
                isActive={activeIndex === index}
                unit={unit}
                onLegendClick={() => onLegendClick && onLegendClick(item.name)}
                suffix={suffix}
              />
            </div>
          ))}
        </div>

        {shouldShowExpand && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full mt-4 py-3 flex items-center justify-center gap-2 text-xs font-bold text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors border border-dashed border-indigo-100"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Toon minder
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Toon {coloredData.length - LIMIT} extra item(s)
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default StatsDonutChart;
